import { io, Socket } from "socket.io-client";

class SocketService {
    private socket: Socket | null = null;
    private eventListeners: Map<string, Function[]> = new Map();

    connect() {
        if (this.socket?.connected) return;

        const socketUrl = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/api$/, "");

        this.socket = io(socketUrl, {
            path: "/socket.io",
            transports: ["websocket", "polling"],
            withCredentials: true,
            autoConnect: true,
        });

        this.socket.on("connect", () => {
            console.log("Socket connected:", this.socket?.id);
            this.notifyListeners("connect", this.socket?.id);
        });

        this.socket.on("disconnect", (reason) => {
            console.log("Socket disconnected:", reason);
            this.notifyListeners("disconnect", reason);
        });

        this.socket.on("connect_error", (error) => {
            console.error("Socket connection error:", error);
            this.notifyListeners("connect_error", error);
        });

        // Listen for generic notification events
        this.socket.on("notification", (data) => {
            console.log("Received notification:", data);
            this.notifyListeners("notification", data);
        });
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    joinUserRoom(userId: string) {
        if (this.socket && userId) {
            this.socket.emit("join", userId);
            console.log(`Joined room: user_${userId}`);
        }
    }

    on(event: string, callback: Function) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event)?.push(callback);

        // If socket is already listening to this event natively, we are covered by the generic listener setup above
        // or we can add specific listeners if needed
    }

    off(event: string, callback: Function) {
        if (this.eventListeners.has(event)) {
            const listeners = this.eventListeners.get(event)?.filter(cb => cb !== callback);
            if (listeners && listeners.length > 0) {
                this.eventListeners.set(event, listeners);
            } else {
                this.eventListeners.delete(event);
            }
        }
    }

    private notifyListeners(event: string, data: any) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.forEach(callback => callback(data));
        }
    }

    isConnected(): boolean {
        return this.socket?.connected || false;
    }
}

export const socketService = new SocketService();
